import { toast } from 'react-toastify';

import {
  endOfToday,
  isAfter,
  isValid,
  isWithinInterval,
  parseISO,
} from 'date-fns';
import { isEqual } from 'lodash';
import clean from 'lodash-clean';
import camelCase from 'lodash/camelCase';
import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';

import i18n from 'shared/lib/i18n';

import { SET_FREQUENCY_STATS, SET_TODAY_COUNT } from './actionConstants';

import { checkHasRole, getRoleIdByName } from './components/Person/utils';

import { roleNames } from './components/Person/constants';

import {
  DATE_RANGES_TYPES,
  INACTIVITY_LOG_OUT_TIME,
  defaultDateFormat,
} from './staticData';

export const loadToken = () => window.localStorage.getItem('jwt');
export const saveToken = (token) => window.localStorage.setItem('jwt', token);
export const removeToken = () => window.localStorage.removeItem('jwt');
export const isAuthenticated = () => !!loadToken();

export const loadUser = () => JSON.parse(window.localStorage.getItem('cu'));
export const saveUser = (user) =>
  window.localStorage.setItem('cu', JSON.stringify(user));
export const removeUser = () => window.localStorage.removeItem('cu');

export const saveServiceData = (data) =>
  window.localStorage.setItem('service', JSON.stringify(data));
export const loadServiceData = () =>
  JSON.parse(window.localStorage.getItem('service')) || {};
export const removeServiceData = () =>
  window.localStorage.removeItem('service');

export const saveTimezonesData = (data) =>
  window.localStorage.setItem('timezones', JSON.stringify(data));
export const loadTimezonesData = () =>
  JSON.parse(window.localStorage.getItem('timezones')) || [];
export const removeTimezonesData = () =>
  window.localStorage.removeItem('timezones');

export const savePinnedBuildings = (data) =>
  window.localStorage.setItem('pinned_buildings', JSON.stringify(data));
export const loadPinnedBuildings = () =>
  JSON.parse(window.localStorage.getItem('pinned_buildings')) || [];
export const removePinnedBuildings = () =>
  window.localStorage.removeItem('pinned_buildings');

export const saveLeftMenuState = (data) =>
  window.localStorage.setItem('is_menu_opened', data);
export const loadIsLeftMenuOpened = () => {
  const isOpened = window.localStorage.getItem('is_menu_opened');
  return isOpened === null || isOpened === 'true';
};

export const saveAdminBuildHash = (data) =>
  window.localStorage.setItem('build_hash', data);
export const loadAdminBuildHash = () =>
  window.localStorage.getItem('build_hash');

export const isValidBuildHash = (value) => value && value !== 'undefined';

export const isAdmin = () =>
  loadUser() && loadUser().isAdmin && (loadUser()?.pmcRoles ?? []).length === 0;

export const isService = () =>
  loadUser() && !loadUser().isAdmin && loadUser().isService;

const getAllUserRoles = (user) => {
  const userProperties = user?.properties || [];
  const userRoles = userProperties?.map((property) => property?.roles) || [];
  return [...new Set(userRoles.flat())];
};

const getUserRolesInProperty = (user, propertyId) => {
  const userProperties = user?.properties || [];
  const property =
    userProperties?.find((property) => property.id === +propertyId) || {};
  const userRolesIdInProperty = property.roles || [];
  return userRolesIdInProperty;
};

const getUserPmcs = (user, pmcId) => {
  const userPmcs = user?.pmcRoles || [];
  user?.pmcRole && userPmcs.push(user.pmcRole);
  if (!pmcId) return userPmcs;
  const userPmc =
    userPmcs?.find((property) => property?.propertyId === +pmcId) || {};
  return [userPmc];
};

const getUserRolesInPmc = (user, pmcId) => {
  const [userPmc] = getUserPmcs(user, pmcId);
  return userPmc?.role ? [userPmc.role] : [];
};

const getUserPermissions = (roles) => {
  const allRoles = loadServiceData().role || [];
  const permissions = [];
  roles.forEach((roleId) => {
    const roleData = allRoles.find((role) => role.id === +roleId);
    roleData && permissions.push(roleData.permissions);
  });
  return [...new Set(permissions.flat())];
};

export const checkHasPermissions = (
  permission,
  user = loadUser() || {},
  propertyId,
) => {
  if (isAdmin()) return true;
  const roles = propertyId
    ? getUserRolesInProperty(user, propertyId)
    : getAllUserRoles(user);
  const permissions = getUserPermissions(roles);
  return permissions.includes(permission);
};

const checkUserRole = (roleId, buildingId, PMCId) => {
  const user = loadUser() || {};

  if (buildingId) {
    return getUserRolesInProperty(user, buildingId).includes(roleId);
  }
  if (PMCId) {
    return getUserRolesInPmc(user, PMCId).includes(roleId);
  }
  return getAllUserRoles(user).includes(roleId);
};

export const getIsReadOnlyAdmin = () => loadUser()?.readOnlyApiAccess;

export const isSuper = (params) => {
  const { buildingId, PMCId } = params || {};
  const roleId = getRoleIdByName(roleNames.superintendent);
  return checkUserRole(roleId, buildingId, PMCId);
};

export const isConcierge = (params) => {
  const { buildingId, PMCId } = params || {};
  const roleId = getRoleIdByName(roleNames.concierge);
  return checkUserRole(roleId, buildingId, PMCId);
};

export const isSuperOrConcierge = (params) =>
  isSuper(params) || isConcierge(params);

const isPropertyManager = (params) => {
  const { buildingId, PMCId } = params || {};
  const roleId = getRoleIdByName(roleNames.property_manager);
  return checkUserRole(roleId, buildingId, PMCId);
};

const isPmcOwner = (params) => {
  const { buildingId, PMCId } = params || {};
  const roleId = getRoleIdByName(roleNames.pmc_owner);
  return checkUserRole(roleId, buildingId, PMCId);
};

export const isPM = (params) => isPropertyManager(params) || isPmcOwner(params);

export const isBuildingPM = (params) => {
  const { PMCId } = params || {};
  const userPmcs = getUserPmcs(loadUser(), PMCId);
  return !!userPmcs.filter(({ isBuildingPm }) => isBuildingPm).length;
};

export const isMasterPM = (params) => {
  const { PMCId } = params || {};
  const userPmcs = getUserPmcs(loadUser(), PMCId);
  return !!userPmcs.filter(({ primary }) => primary).length;
};

export const saveLastActivityTime = () =>
  window.localStorage.setItem('lastActivityTime', Date.now());
export const getLastActivityTime = () =>
  window.localStorage.getItem('lastActivityTime') || Date.now();
export const isSessionExpired = () => {
  if (!isAuthenticated()) return false;
  const lastActivityTime = getLastActivityTime();
  const timeDifference = Date.now() - lastActivityTime;
  return timeDifference > INACTIVITY_LOG_OUT_TIME;
};

export const avatarPlaceholderSRC =
  'https://carson-public-assets.s3.us-east-2.amazonaws.com/icons/8c6f8a628822434094599c3b5f74642f.png';

export const getUserTimezone = (useUserTimezone = true) =>
  useUserTimezone && Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getIsMobileScreen = () =>
  window.innerWidth > 0 && window.innerWidth < 600;

export const getToday = () => moment().startOf('day');
export const getTodayInUserTimezone = () =>
  moment.tz(moment().startOf('day'), getUserTimezone());

export const isToday = (rawDate) => {
  const zone = getUserTimezone();
  if (!rawDate || !zone) return null;
  const today = getToday();
  const date = moment.tz(rawDate, zone);
  return date.isSame(today, 'd');
};

export const isWithinAWeek = (rawDate) => {
  const zone = getUserTimezone();
  if (!rawDate || !zone) return null;
  const aWeakOld = moment().subtract(6, 'days').startOf('day');
  const date = moment.tz(rawDate, zone);
  return date.isAfter(aWeakOld);
};

export const getDaysDiff = (rawDate) => {
  const zone = getUserTimezone();
  if (!rawDate || !zone) return null;
  const today = moment().startOf('day');
  const date = moment.tz(rawDate, zone);
  return date.diff(today, 'days');
};

export const formatDate = (
  rawDate,
  useUserTimezone = true,
  format = defaultDateFormat,
) => {
  if (!rawDate) return '';
  const zone = getUserTimezone(useUserTimezone);
  let date = null;
  if (format === null && zone) {
    date = moment(rawDate).parseZone().format();
  } else if (zone) {
    date = moment.tz(rawDate, zone).format(format);
  } else {
    date = moment(rawDate).parseZone().format(format);
  }
  return date;
};

export const dateToLocaleString = (
  date,
  isUnix = false,
  format = 'short',
  options = null,
  lowerCase = true,
) => {
  let formattedDate = '';
  if (date) {
    const dateObj =
      isUnix && typeof date === 'number'
        ? new Date(date * 1000)
        : new Date(date);
    if (!Number.isNaN(new Date(date).valueOf()))
      if (options) {
        formattedDate = dateObj.toLocaleString('en-us', options);
      } else {
        formattedDate = dateObj
          .toLocaleString('en-us', { dateStyle: format, timeStyle: format })
          .replaceAll(',', '');
      }
    formattedDate = lowerCase
      ? formattedDate.toLocaleLowerCase()
      : formattedDate;
  }
  return formattedDate;
};

export const formatDateFixedTimezone = (
  rawDate,
  timezone,
  format = defaultDateFormat,
) => {
  if (!rawDate || !timezone) return null;
  return moment(rawDate).tz(timezone).format(format);
};

export const dateFromNow = (rawDate) => {
  if (!rawDate) return '';
  const zone = getUserTimezone();
  let date = null;
  if (zone) {
    date = moment(rawDate).parseZone().fromNow();
  } else {
    date = moment(rawDate).parseZone().fromNow();
  }
  return date;
};

export const formatRole = (roleName) => {
  if (!roleName) {
    return roleName;
  }
  if (roleName === 'pmc_owner') return i18n.t('roles.pmcOwner');
  if (roleName.indexOf('_') === -1) {
    return capitalize(roleName);
  }
  let roleNameArray = roleName.split('_');
  roleNameArray = roleNameArray.map(
    (r) => r.charAt(0).toUpperCase() + r.slice(1),
  );
  return roleNameArray.join(' ');
};
export const formatStaffRole = (roleName, isPrimaryStaff) =>
  isPrimaryStaff
    ? `${formatRole(roleName)} \n ${i18n.t('title.primaryStaff')}`
    : formatRole(roleName);

export const roleTypeToName = (roleList, roleId) => {
  if (!isEmpty(roleList)) {
    const role = find(roleList, (role) => role.id === roleId);
    return role.name;
  }
  return null;
};

export const camelCaseToSpaces = (text) =>
  text.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

export const filterObjectByIds = (object, listOfIds) => {
  const filtered = {};
  if (
    !listOfIds ||
    !listOfIds.length ||
    !object ||
    !Object.values(object).length
  ) {
    return filtered;
  }
  listOfIds.forEach((id) => {
    if (object[id]) {
      filtered[id] = object[id];
    }
  });
  return filtered;
};

export const searchObjectByKeyValue = (object, key, searchValue) => {
  const filtered = {};
  if (isEmpty(object) || !key || !searchValue) return filtered;
  Object.keys(object).forEach((currentKey) => {
    const keyValue = object[currentKey]?.[key]?.toLowerCase();
    if (keyValue.includes(searchValue.toLowerCase())) {
      filtered[currentKey] = object[currentKey];
    }
  });
  return filtered;
};

export const filterObjectByKeyValue = (object, key, value) => {
  const filtered = {};
  if (isEmpty(object) || !key || !value) return filtered;
  Object.keys(object).forEach((objectKey) => {
    const item = object[objectKey];
    const comparedValue = get(item, key, null);
    if (comparedValue === value) {
      filtered[objectKey] = item;
    }
  });
  return filtered;
};

export const filterObjectByKeyValues = (object, key, values) => {
  const filtered = {};
  if (!isEmpty(object) && key && values?.length) {
    Object.entries(object).forEach(([objectKey, objectValue]) => {
      const comparedValue = get(objectValue, key, null);
      if (comparedValue && values.includes(comparedValue)) {
        filtered[objectKey] = object[objectKey];
      }
    });
  }
  return filtered;
};

export const findObjectInArrayById = (array, id) =>
  array.find((item) => String(item.id) === String(id));

export const logoUrl = require('./res/images/CarsonLogo.png');

export const infoIcon = require('./res/images/info-icon.png');

export const darkLogoUrl = require('./res/images/carson-logo-dark@2x.png');

// TODO: this should work just by filtering roles arr - API changes needed
const getStaffRoleIds = () => {
  const staffRoleIds = [];
  const allRoles = loadServiceData().role || [];
  allRoles.forEach(
    ({ id, isStaff }) =>
      isStaff && !checkHasRole(roleNames.broker, id) && staffRoleIds.push(id),
  );
  return staffRoleIds;
};
const getResidentRoleIds = () => {
  const allRoles = loadServiceData().role || [];
  return allRoles
    .filter(({ type }) => type === 'resident' || type === 'board_member')
    .map(({ id }) => id);
};
const getVisitorRoleIds = () => {
  const allRoles = loadServiceData().role || [];
  return allRoles
    .filter(
      ({ type, id }) =>
        type === 'visitor' || checkHasRole(roleNames.broker, id),
    )
    .map(({ id }) => id);
};

export const isStaffRole = (id) => getStaffRoleIds().includes(id);
export const isResidentRole = (id) => getResidentRoleIds().includes(id);
export const isVisitorRole = (id) => getVisitorRoleIds().includes(id);

export const getUniqueValuesByKey = (items, key) => {
  if (!items || !items.length) {
    return [];
  }
  const values = items.map((item) => item[key]);
  return [...new Set(values)];
};

export const sortByKey = (a, b, key) => {
  const textA = String(get(a, key, '')).toLowerCase();
  const textB = String(get(b, key, '')).toLowerCase();
  if (textA < textB) {
    return -1;
  }
  if (textA > textB) {
    return 1;
  }
  return 0;
};

export const customSorting = (valueA, valueB) => {
  let a = valueA;
  let b = valueB;
  const dateRegEx = /(\d{2})\/(\d{2})\/(\d{2})/;
  const isDate = (value) => dateRegEx.test(value);

  if (isDate(a) && isDate(b)) {
    return new Date(b) - new Date(a);
  }

  a = a === null || a === undefined ? '\uFFFF' : a;
  b = b === null || b === undefined ? '\uFFFF' : b;

  a = typeof a === 'string' ? a.toLowerCase().replace(/\s+/g, '') : a;
  b = typeof b === 'string' ? b.toLowerCase().replace(/\s+/g, '') : b;

  a = a === 'other' || a === '' ? '\uFFFF' : a;
  b = b === 'other' || b === '' ? '\uFFFF' : b;

  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }

  return 0;
};

export const formatEntries = (entries) =>
  Array.isArray(entries) ? entries : [];
export const extractNumbersFromText = (text) =>
  Number(text.toString().match(/\d+$/));

export const getSpaces = (num) => {
  const spaces = [];
  for (let i = 0; i < num; i += 1) {
    spaces.push(<span key={i}>{String.fromCharCode(160)}</span>);
  }
  return spaces;
};

export const deleteUnderscore = (string) => string && string.replace(/_/g, '');
export const deleteSpaces = (string) => string && string.replace(/\s+/g, '');

export const replaceUnderscoreWithSpace = (string) =>
  string && string.replace(/_/g, ' ');

export const replaceUnderscoreWithDash = (string) =>
  string && string.replace(/_/g, '-');

export const replaceUppercaseWithUnderscore = (string) =>
  string && string.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);

export const toSentenceCase = (string) => {
  if (!string) return '';
  const itemString = typeof string === 'string' ? string : string.toString();
  return replaceUnderscoreWithSpace(
    itemString.charAt(0).toUpperCase() +
      itemString.replace(/([a-z])([A-Z])/g, '$1 $2').substring(1),
  );
};

export const toCamelCaseWithSpaces = (string) => {
  if (!string) return '';
  return camelCase(replaceUnderscoreWithSpace(string))
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
};

export const getLaunchDateFromLocation = (location, buildings) => {
  if (!location || isEmpty(buildings)) return null;
  const numb = location.match(/\d/g);
  if (!numb) return null;
  const buildingId = numb.join('');
  const building = buildings[buildingId];
  const launchDate = get(building, 'buildingData.launchDate', null);
  return launchDate;
};

export const openUrlInNewTab = (url) => {
  const win = window.open(url);
  win && win.focus();
};

export const filterBuildingsWithExternalSR = (rawBuildings) => {
  const buildings = {};
  if (isEmpty(rawBuildings)) return buildings;
  Object.keys(rawBuildings).forEach((buildingId) => {
    const building = rawBuildings?.[buildingId];
    if (building?.externalServiceRequest) return;
    buildings[buildingId] = building;
  });
  return buildings;
};

export const isInViewport = (element) => {
  const bounding = element.getBoundingClientRect();
  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <=
      (window.innerWidth || document.documentElement.clientWidth)
  );
};
export const getUniqueValues = (array, key) => {
  if (!array || !array.length) return [];
  const values = [...array];
  const result = [];
  const map = new Map();
  values.forEach((item) => {
    if (!map.has(item[key])) {
      map.set(item[key], true);
      result.push(item);
    }
  });
  return result;
};

export const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// to get the todayCount value:
// 1. update queryParams - add todayCount = true
// 2. dispatch the same action as for getting items
// 3. when setting a url for a request check if there is a value from #1
// 4. if it exists - update the request url
// 5. in middleware check if res has todayCount value
// 6. if true - dispatch the SET_TODAY_COUNT action
// 7. reset todayCount value on every @@router/LOCATION_CHANGE action

export const getTodayCount = (action, dispatch, queryParams, ...args) => {
  if (!action || !dispatch || !queryParams) return null;
  const filteredParams = { todayCount: true, timezone: getUserTimezone() };
  Object.keys(queryParams).forEach((param) => {
    const isDateRangeType = Object.keys(DATE_RANGES_TYPES).find((type) =>
      param.includes(DATE_RANGES_TYPES[type]),
    );
    if (isDateRangeType) return;
    filteredParams[param] = queryParams[param];
  });
  return action(dispatch, filteredParams, ...args);
};

export const shouldSkipTodayCountReq = (res, queryParams) => {
  let startDate = null;
  let endDate = null;

  Object.keys(queryParams).forEach((key) => {
    const isDateRangeType = Object.keys(DATE_RANGES_TYPES).find((type) =>
      key.includes(DATE_RANGES_TYPES[type]),
    );
    if (!isDateRangeType) return;

    if (key.includes('_after')) {
      startDate = queryParams[key];
    }
    if (key.includes('_before')) {
      endDate = queryParams[key];
    }
  });

  const start =
    startDate && isValid(parseISO(startDate))
      ? parseISO(startDate)
      : new Date(2000, 0);
  const end =
    endDate && isValid(parseISO(endDate)) ? parseISO(endDate) : endOfToday();

  const isIncludesTodayRecords =
    start &&
    end &&
    isAfter(start, end) &&
    isWithinInterval(new Date(), { start, end });
  const recordsCount = res?.data?.count || res?.count;
  const hasRecordsCount = recordsCount > 0;
  return isIncludesTodayRecords && !hasRecordsCount;
};

export const getFrequencyStats = (
  action,
  dispatch,
  queryParams,
  useFrequencyUrl = true,
  ...args
) => {
  if (!action || !dispatch || !queryParams) return null;
  const filteredParams = { frequencyStats: true, useFrequencyUrl };
  Object.keys(queryParams).forEach((param) => {
    // filter params here if needed
    filteredParams[param] = queryParams[param];
  });
  return action(dispatch, clean(filteredParams), ...args);
};

export const formatNumber = (value) => {
  if (Number.isNaN(Number(value))) return value;
  return new Intl.NumberFormat().format(value);
};

export const printContent = (
  contentEl = 'report_area',
  targetEl = 'print_area',
) => {
  const printContents = document.getElementById(contentEl).innerHTML;
  const targetElement = document.getElementById(targetEl);
  targetElement.innerHTML = printContents;
  window.print();
  targetElement.innerHTML = '';
};

export const reloadPage = (isReloadFromServer) =>
  window.location.reload(isReloadFromServer);

export const checkIfInIframe = () => window.location !== window.parent.location;

export const getSelectValue = (value, options) => {
  if (!value) {
    return null;
  }
  if (typeof value === 'object' && value !== null) {
    return value;
  }
  if (
    (typeof value === 'string' || typeof value === 'number') &&
    options?.length > 0
  ) {
    return options.find((option) => String(option?.value) === String(value));
  }
  return null;
};

export const notifyAboutNewVersion = (currentBuildHash) => {
  saveAdminBuildHash(currentBuildHash);
  toast.warn(i18n.t('actions.wait'), {
    autoClose: 10000,
    hideProgressBar: true,
    closeOnClick: false,
    pauseOnHover: false,
  });
  setTimeout(() => {
    reloadPage(true);
  }, 6000);
};

export const getRandomIntegerFromInterval = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const getInterceptorAction = (action) => {
  const data = action?.res?.data || {};
  const { todayCount, frequencyStats } = data;
  const isTodayCount = todayCount >= 0;
  const isFrequencyStats =
    frequencyStats && Object.keys(frequencyStats).length >= 0;

  if (isTodayCount) {
    return {
      type: SET_TODAY_COUNT,
      todayCount,
    };
  }

  if (isFrequencyStats) {
    return {
      type: SET_FREQUENCY_STATS,
      frequencyStats,
    };
  }

  return null;
};

export const getIsTouchDevice = () =>
  'ontouchstart' in window ||
  navigator.maxTouchPoints > 0 ||
  navigator.msMaxTouchPoints > 0;

// https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
export const isSafari =
  /constructor/i.test(window.HTMLElement) ||
  ((p) => p.toString() === '[object SafariRemoteNotification]')(
    !window.safari ||
      (typeof window.safari !== 'undefined' && window.safari.pushNotification),
  );

export const isChrome = () => {
  const isChromium = window.chrome;
  const winNav = window.navigator;
  const vendorName = winNav.vendor;
  const isOpera = typeof window.opr !== 'undefined';
  const isIEedge = winNav.userAgent.indexOf('Edg') > -1;
  const isIOSChrome = winNav.userAgent.match('CriOS');

  if (isIOSChrome) {
    // is Google Chrome on IOS
  } else if (
    isChromium !== null &&
    typeof isChromium !== 'undefined' &&
    vendorName === 'Google Inc.' &&
    isOpera === false &&
    isIEedge === false
  ) {
    // is Google Chrome
    return true;
  } else {
    // not Google Chrome
  }
  return false;
};

/**
 * @param {object} obj Any object
 * @returns {boolean} `false` for empty object
 */
export const filterOutEmptyObjects = (obj) => Object.values(obj).length > 0;

// Difference in minutes between two dates
export const diffTimeInMinutes = (time1, time2) => {
  const ms = Math.abs(new Date(time1).getTime() - new Date(time2).getTime());
  return Math.ceil(ms / 60000);
};

export const scrollIntoView = (elementId) =>
  document.getElementById(elementId)?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'center',
  });

export const getIsOverflown = (element) => {
  if (!element?.clientHeight) return false;
  const childElement = element.firstChild;
  // add 10px to the container height to ignore a few pixels diff
  return element.clientHeight + 10 < childElement?.clientHeight;
};

export const getIsAccessCardsShown = () => {
  if (isAdmin()) return true;
  if (!isPM(null, true)) return false;

  const associatedWithLyazon = (loadUser().properties ?? []).find(
    ({ hasLyazonAccounts }) => hasLyazonAccounts,
  );

  return Boolean(associatedWithLyazon);
};

export const arePropsEqual = (prevProps, nextProps) =>
  isEqual(prevProps, nextProps);
